import React from 'react';
import Icon from '../../../components/AppIcon';
import CustomerActionsMenu from './CustomerActionsMenu';
import { formatCurrency } from '../../../utils/currency';

const CustomerTable = ({ 
  customers, 
  sortConfig,
  onSort,
  onViewProfile,
  onEdit,
  onDelete,
  onSendStatement,
  onViewPayments
}) => {
  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) {
      return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={16} className="text-primary" />
      : <Icon name="ChevronDown" size={16} className="text-primary" />;
  };

  const getBalanceColor = (direction) => {
    if (!direction) return 'text-foreground';
    const d = direction.toLowerCase();
    if (d.includes('owe') && d.includes('customer')) return 'text-warning';
    if (d.includes('customer owes')) return 'text-success';
    return 'text-foreground';
  };

  const totals = (customers || []).reduce((acc, c) => ({
    openingBalance: acc.openingBalance + (c.openingBalance || 0),
    invoiceAmount:  acc.invoiceAmount  + (c.invoiceAmount  || 0),
    invoiceCount:   acc.invoiceCount   + (c.invoiceCount   || 0),
    paymentAmount:  acc.paymentAmount  + (c.paymentAmount  || 0),
    paymentCount:   acc.paymentCount   + (c.paymentCount   || 0),
    endingBalance:  acc.endingBalance  + (c.endingBalance  || 0),
  }), { openingBalance: 0, invoiceAmount: 0, invoiceCount: 0, paymentAmount: 0, paymentCount: 0, endingBalance: 0 });

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('customerName')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Customer Name</span>
                  {getSortIcon('customerName')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('contactPerson')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Contact Person</span>
                  {getSortIcon('contactPerson')}
                </div>
              </th>
              <th className="px-4 py-3 text-right">
                <div className="font-heading font-semibold text-sm text-foreground">Opening</div>
              </th>
              <th className="px-4 py-3 text-right">
                <div className="font-heading font-semibold text-sm text-foreground">Invoices</div>
              </th>
              <th className="px-4 py-3 text-right">
                <div className="font-heading font-semibold text-sm text-foreground">Payments</div>
              </th>
              <th 
                className="px-4 py-3 text-right cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('endingBalance')}
              >
                <div className="flex items-center justify-end gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Ending Balance</span>
                  {getSortIcon('endingBalance')}
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="font-heading font-semibold text-sm text-foreground">
                  Payment Terms
                </div>
              </th>
              <th className="px-4 py-3 text-right">
                <div className="font-heading font-semibold text-sm text-foreground">
                  Actions
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {customers?.map((customer) => (
              <tr key={customer?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="font-body font-medium text-sm text-foreground">
                      {customer?.customerName}
                    </span>
                    <span className="font-body text-xs text-muted-foreground">
                      {customer?.email}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="font-body text-sm text-foreground">
                      {customer?.contactPerson}
                    </span>
                    <span className="font-body text-xs text-muted-foreground">
                      {customer?.phone}
                    </span>
                  </div>
                </td>
                {/* Opening */}
                <td className="px-4 py-3 text-right">
                  <span className="font-body text-sm text-foreground">
                    {formatCurrency(customer?.openingBalance)}
                  </span>
                </td>
                {/* Invoices */}
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-col items-end">
                    <span className="font-body text-sm text-foreground">{formatCurrency(customer?.invoiceAmount)}</span>
                    <span className="font-body text-xs text-muted-foreground">{customer?.invoiceCount} inv</span>
                  </div>
                </td>
                {/* Payments */}
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-col items-end">
                    <span className="font-body text-sm text-success">{formatCurrency(customer?.paymentAmount)}</span>
                    <span className="font-body text-xs text-muted-foreground">{customer?.paymentCount} pmt</span>
                  </div>
                </td>
                {/* Ending Balance */}
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-col items-end">
                    <span className={`font-body font-semibold text-sm ${getBalanceColor(customer?.balanceDirection)}`}>
                      {formatCurrency(customer?.endingBalance)}
                    </span>
                    <span className="font-body text-xs text-muted-foreground">{customer?.balanceDirection}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="font-body text-sm text-foreground">
                    {customer?.paymentTerms}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <CustomerActionsMenu
                    customer={customer}
                    onViewProfile={onViewProfile}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onSendStatement={onSendStatement}
                    onViewPayments={onViewPayments}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-muted border-t-2 border-primary">
              <td className="px-4 py-3" colSpan={2}>
                <span className="font-heading font-bold text-sm text-foreground">
                  Totals — {customers?.length} customer{customers?.length !== 1 ? 's' : ''}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <span className="font-heading font-bold text-sm text-foreground">
                  {formatCurrency(totals.openingBalance)}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="font-heading font-bold text-sm text-foreground">{formatCurrency(totals.invoiceAmount)}</span>
                  <span className="font-body text-xs text-muted-foreground">{totals.invoiceCount} inv</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="font-heading font-bold text-sm text-success">{formatCurrency(totals.paymentAmount)}</span>
                  <span className="font-body text-xs text-muted-foreground">{totals.paymentCount} pmt</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <span className="font-heading font-bold text-sm text-foreground">
                  {formatCurrency(totals.endingBalance)}
                </span>
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default CustomerTable;